import {
  Button,
  CardListItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorListItem,
  List,
  Typography,
} from '@razz-team/ui-web';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { ApiCard } from '../../../openapi';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useCardTerminateMutation } from '../../../api/cards';
import { Route } from '../../../routes/account.cards_.$cardId';

export function TerminateCardDialog({
  open,
  card,
  onCancel,
}: {
  open: boolean;
  card: ApiCard;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const idempotencyKey = useIdempotencyKey();
  const terminateCardMutation = useCardTerminateMutation({ cardId: card.id, idempotencyKey });
  const navigate = Route.useNavigate();

  const handleCancel = useCallback(() => {
    terminateCardMutation.reset();
    onCancel();
  }, [terminateCardMutation, onCancel]);

  const handleConfirm = useCallback(() => {
    terminateCardMutation.mutate();
  }, [terminateCardMutation]);

  useEffect(() => {
    if (terminateCardMutation.isSuccess) {
      terminateCardMutation.reset();
      navigate({
        to: '/account/home',
        replace: true,
      });
    }
  }, [terminateCardMutation, navigate, onCancel]);

  return (
    <Dialog open={open} fullWidth maxWidth="sm" onClose={handleCancel}>
      <DialogTitle>{t(`Terminate the card?`)}</DialogTitle>
      <DialogContent>
        <Typography>
          {t(
            `You won't longer be able to see this card in the {{productName}} or use it for payments.`,
          )}
        </Typography>
        <List>
          <CardListItem card={card} />
          {terminateCardMutation.isError ? (
            <ErrorListItem error={terminateCardMutation.error} />
          ) : null}
        </List>
      </DialogContent>
      <DialogActions>
        <Button autoFocus color="secondary" onClick={handleCancel}>
          {t('Cancel')}
        </Button>
        <Button color="error" loading={terminateCardMutation.isPending} onClick={handleConfirm}>
          {t('Close card')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
